<template>
  <div class="trip-form-page">
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h1 class="h3 mb-1">
          {{ isEditing ? "Edit Trip" : "Create New Trip" }}
        </h1>
        <p class="text-muted mb-0">
          {{ isEditing ? "Update trip details" : "Add a new travel package" }}
        </p>
      </div>
      <router-link to="/admin/trips" class="btn btn-outline-secondary">
        <i class="fas fa-arrow-left me-2"></i>
        Back to Trips
      </router-link>
    </div>

    <!-- Trip Form -->
    <div class="row">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Trip Details</h5>
          </div>
          <div class="card-body">
            <form @submit.prevent="handleSubmit">
              <!-- Basic Information -->
              <div class="row mb-3">
                <div class="col-md-6">
                  <label for="place" class="form-label">Destination *</label>
                  <input
                    type="text"
                    class="form-control"
                    id="place"
                    v-model="form.place"
                    :class="{ 'is-invalid': errors.place }"
                    placeholder="Enter destination name"
                    required
                  />
                  <div v-if="errors.place" class="invalid-feedback">
                    {{ errors.place }}
                  </div>
                </div>

                <div class="col-md-6">
                  <label for="startDate" class="form-label">Start Date (Optional)</label>
                  <input
                    type="date"
                    class="form-control"
                    id="startDate"
                    v-model="form.startDate"
                    :class="{ 'is-invalid': errors.startDate }"
                    :min="new Date().toISOString().split('T')[0]"
                  />
                  <div v-if="errors.startDate" class="invalid-feedback">
                    {{ errors.startDate }}
                  </div>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <label for="endDate" class="form-label">End Date (Optional)</label>
                  <input
                    type="date"
                    class="form-control"
                    id="endDate"
                    v-model="form.endDate"
                    :class="{ 'is-invalid': errors.endDate }"
                    :min="form.startDate || new Date().toISOString().split('T')[0]"
                  />
                  <div v-if="errors.endDate" class="invalid-feedback">
                    {{ errors.endDate }}
                  </div>
                </div>

                <div class="col-md-6">
                  <label for="price" class="form-label">Price (INR) *</label>
                  <input
                    type="number"
                    class="form-control"
                    id="price"
                    v-model.number="form.price"
                    :class="{ 'is-invalid': errors.price }"
                    placeholder="Enter price"
                    min="1"
                    step="0.01"
                    required
                  />
                  <div v-if="errors.price" class="invalid-feedback">
                    {{ errors.price }}
                  </div>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-6">
                  <label for="days" class="form-label">Duration *</label>
                  <input
                    type="text"
                    class="form-control"
                    id="days"
                    v-model="form.days"
                    :class="{ 'is-invalid': errors.days }"
                    placeholder="e.g., 4N/3D, 7 days, 2 weeks"
                    required
                  />
                  <div v-if="errors.days" class="invalid-feedback">
                    {{ errors.days }}
                  </div>
                  <div class="form-text">
                    Enter duration in any format (e.g., 4N/3D, 7 days, 2 weeks, 1 month, flexible)
                  </div>
                </div>

                <div class="col-md-6">
                  <label for="groupSize" class="form-label">Group Size (Optional)</label>
                  <input
                    type="number"
                    class="form-control"
                    id="groupSize"
                    v-model.number="form.groupSize"
                    :class="{ 'is-invalid': errors.groupSize }"
                    placeholder="e.g., 10, 15, 20"
                    min="1"
                    step="1"
                  />
                  <div v-if="errors.groupSize" class="invalid-feedback">
                    {{ errors.groupSize }}
                  </div>
                  <div class="form-text">
                    Leave empty for flexible group size
                  </div>
                </div>
              </div>

              <!-- Description -->
              <div class="mb-3">
                <label for="description" class="form-label"
                  >Description *</label
                >
                <textarea
                  class="form-control"
                  id="description"
                  v-model="form.description"
                  :class="{ 'is-invalid': errors.description }"
                  rows="4"
                  placeholder="Describe the trip highlights and details"
                  required
                ></textarea>
                <div v-if="errors.description" class="invalid-feedback">
                  {{ errors.description }}
                </div>
              </div>

              <!-- Google Sheet URL -->
              <div class="mb-3">
                <label for="googleSheetUrl" class="form-label"
                  >Google Sheet URL *</label
                >
                <input
                  type="url"
                  class="form-control"
                  id="googleSheetUrl"
                  v-model="form.googleSheetUrl"
                  :class="{ 'is-invalid': errors.googleSheetUrl }"
                  placeholder="https://docs.google.com/spreadsheets/..."
                  required
                />
                <div v-if="errors.googleSheetUrl" class="invalid-feedback">
                  {{ errors.googleSheetUrl }}
                </div>
                <div class="form-text">
                  Link to the Google Sheet for payment tracking
                </div>
              </div>

              <!-- File Uploads Section -->
              <div class="mb-4">
                <h6>Media Files</h6>

                <!-- Images Upload -->
                <div class="mb-3">
                  <label class="form-label">Images (Max 10)</label>
                  <FileUpload
                    :multiple="true"
                    :max-files="10"
                    accept="image/*"
                    :trip-id="tripId"
                    @files-selected="handleImagesSelected"
                    :existing-files="form.images"
                  />
                  <div class="form-text">
                    Upload up to 10 images. Supported formats: JPG, PNG, WebP,
                    HEIC (Max 50MB each)
                  </div>
                </div>

                <!-- Video Upload -->
                <div class="mb-3">
                  <label class="form-label">Video (Optional)</label>
                  <FileUpload
                    :multiple="false"
                    accept="video/*"
                    :trip-id="tripId"
                    @files-selected="handleVideoSelected"
                    :existing-files="form.video ? [form.video] : []"
                  />
                  <div class="form-text">
                    Upload one video file. Supported formats: MP4, MOV, AVI,
                    WMV, FLV, WebM (Max 100MB)
                  </div>
                </div>

                <!-- PDF Upload -->
                <div class="mb-3">
                  <label class="form-label">Itinerary PDF (Optional)</label>
                  <FileUpload
                    :multiple="false"
                    accept=".pdf,application/pdf"
                    :trip-id="tripId"
                    @files-selected="handlePdfSelected"
                    :existing-files="
                      form.itineraryPdf ? [form.itineraryPdf] : []
                    "
                  />
                  <div class="form-text">
                    Upload itinerary PDF file (Max 25MB)
                  </div>
                </div>
              </div>

              <!-- Error Display -->
              <div v-if="error" class="alert alert-danger" role="alert">
                <i class="fas fa-exclamation-triangle me-2"></i>
                {{ error }}
              </div>

              <!-- Form Actions -->
              <div class="d-flex justify-content-end gap-2">
                <router-link to="/admin/trips" class="btn btn-secondary">
                  Cancel
                </router-link>
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="isLoading"
                >
                  <span
                    v-if="isLoading"
                    class="spinner-border spinner-border-sm me-2"
                    role="status"
                  >
                    <span class="visually-hidden">Loading...</span>
                  </span>
                  <i
                    v-else
                    :class="isEditing ? 'fas fa-save' : 'fas fa-plus'"
                    class="me-2"
                  ></i>
                  {{
                    isLoading
                      ? "Saving..."
                      : isEditing
                      ? "Update Trip"
                      : "Create Trip"
                  }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Preview Panel -->
      <div class="col-lg-4">
        <div class="card">
          <div class="card-header">
            <h6 class="mb-0">Preview</h6>
          </div>
          <div class="card-body">
            <div class="trip-preview">
              <h6>{{ form.place || "Destination Name" }}</h6>
              <div class="mb-2">
                <small class="text-muted">
                  <i class="fas fa-calendar me-1"></i>
                  {{ displayDates }}
                </small>
              </div>
              <div class="mb-2">
                <small class="text-muted">
                  <i class="fas fa-clock me-1"></i>
                  {{ form.days || "Duration" }}
                </small>
              </div>
              <div class="mb-2">
                <small class="text-muted">
                  <i class="fas fa-users me-1"></i>
                  {{ displayGroupSize }}
                </small>
              </div>
              <div class="mb-3">
                <span class="h6 text-primary"> ₹{{ form.price || 0 }} </span>
              </div>
              <p class="small text-muted">
                {{ form.description || "Trip description will appear here..." }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Swal from "sweetalert2";
import FileUpload from "@/components/FileUpload.vue";

export default {
  name: "TripForm",
  components: {
    FileUpload,
  },
  props: {
    id: String,
  },
  data() {
    return {
      form: {
        place: "",
        startDate: "",
        endDate: "",
        price: "",
        days: "",
        groupSize: "",
        description: "",
        googleSheetUrl: "",
        images: [],
        video: null,
        itineraryPdf: null,
      },
      errors: {},
      error: null,
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters("trips", ["currentTrip"]),

    isEditing() {
      return !!this.id;
    },

    tripId() {
      return this.id;
    },

    displayDates() {
      if (this.form.startDate && this.form.endDate) {
        const startDate = new Date(this.form.startDate).toLocaleDateString('en-IN');
        const endDate = new Date(this.form.endDate).toLocaleDateString('en-IN');
        return `${startDate} - ${endDate}`;
      } else if (this.form.startDate && !this.form.endDate) {
        const startDate = new Date(this.form.startDate).toLocaleDateString('en-IN');
        return `${startDate} - Flexible end date`;
      } else if (!this.form.startDate && this.form.endDate) {
        const endDate = new Date(this.form.endDate).toLocaleDateString('en-IN');
        return `Flexible start date - ${endDate}`;
      } else {
        return 'Flexible dates';
      }
    },

    displayGroupSize() {
      if (this.form.groupSize && this.form.groupSize > 0) {
        return `${this.form.groupSize} people`;
      } else {
        return 'Flexible group size';
      }
    },


  },
  methods: {
    ...mapActions("trips", [
      "createTrip",
      "updateTrip",
      "fetchTripById",
      "clearCurrentTrip",
    ]),

    validateForm() {
      this.errors = {};

      if (!this.form.place.trim()) {
        this.errors.place = "Destination is required";
      }

      // Validate date range if both dates are provided
      if (this.form.startDate && this.form.endDate) {
        const start = new Date(this.form.startDate);
        const end = new Date(this.form.endDate);
        if (end <= start) {
          this.errors.endDate = "End date must be after start date";
        }
      }

      if (!this.form.price || this.form.price <= 0) {
        this.errors.price = "Price must be greater than 0";
      }

      // Convert to string and trim for validation
      const daysValue = this.form.days ? String(this.form.days).trim() : '';

      if (!daysValue) {
        this.errors.days = "Duration is required";
      } else if (daysValue.length < 2) {
        this.errors.days = "Duration must be at least 2 characters long";
      } else if (daysValue.length > 50) {
        this.errors.days = "Duration must be less than 50 characters";
      } else {
        // Validate duration format (e.g., "4N/3D", "7 days", "2 weeks", "1 month")
        const durationPattern = /^(\d+[NnDd]\/\d+[NnDd]|\d+\s*(days?|weeks?|months?|years?)|flexible|custom)$/i;
        if (!durationPattern.test(daysValue)) {
          this.errors.days = "Please enter a valid duration format (e.g., '4N/3D', '7 days', '2 weeks')";
        }
      }

      if (this.form.groupSize && (this.form.groupSize < 1 || !Number.isInteger(this.form.groupSize))) {
        this.errors.groupSize = "Group size must be a positive integer";
      }

      if (!this.form.description.trim()) {
        this.errors.description = "Description is required";
      }

      if (!this.form.googleSheetUrl.trim()) {
        this.errors.googleSheetUrl = "Google Sheet URL is required";
      }

      return Object.keys(this.errors).length === 0;
    },

    async handleSubmit() {
      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        if (this.isEditing) {
          await this.updateTrip({
            id: this.id,
            tripData: this.form,
          });
        } else {
          await this.createTrip(this.form);
        }

        // Show success message
        await Swal.fire({
          title: "Success!",
          text: `Trip ${this.isEditing ? "updated" : "created"} successfully`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });

        // Redirect to trips list
        this.$router.push("/admin/trips");
      } catch (error) {
        this.error = error.message;

        Swal.fire({
          title: "Error",
          text: error.message,
          icon: "error",
          confirmButtonColor: "#2c5aa0",
        });
      } finally {
        this.isLoading = false;
      }
    },

    handleImagesSelected(files) {
      // All files are now URLs (uploaded via presigned URLs)
      this.form.images = files.filter((file) => typeof file === "string");
    },

    handleVideoSelected(files) {
      // All files are now URLs (uploaded via presigned URLs)
      const urlFiles = files.filter((file) => typeof file === "string");
      this.form.video = urlFiles.length > 0 ? urlFiles[0] : null;
    },

    handlePdfSelected(files) {
      // All files are now URLs (uploaded via presigned URLs)
      const urlFiles = files.filter((file) => typeof file === "string");
      this.form.itineraryPdf = urlFiles.length > 0 ? urlFiles[0] : null;
    },

    async loadTripData() {
      if (this.isEditing) {
        try {
          await this.fetchTripById(this.id);
          const trip = this.currentTrip;

          if (trip) {
            this.form = {
              place: trip.place,
              startDate: trip.startDate || '',
              endDate: trip.endDate || '',
              price: trip.price,
              days: trip.days,
              groupSize: trip.groupSize || '',
              description: trip.description,
              googleSheetUrl: trip.googleSheetUrl,
              images: [],
              video: null,
              itineraryPdf: null,
            };

            // Set existing files for preview
            this.form.images = trip.images || [];
            this.form.video = trip.video || null;
            this.form.itineraryPdf = trip.itineraryPdf || null;
          }
        } catch (error) {
          console.error("Error loading trip:", error);
          this.$router.push("/admin/trips");
        }
      }
    },
  },
  async created() {
    await this.loadTripData();
  },
  beforeUnmount() {
    this.clearCurrentTrip();
  },
};
</script>

<style scoped>
.trip-form-page {
  animation: fadeIn 0.5s ease-out;
}

.trip-preview {
  background: var(--light-color);
  padding: 1rem;
  border-radius: var(--border-radius);
  border: 1px solid #dee2e6;
}

@media (max-width: 768px) {
  .trip-form-page .col-lg-4 {
    margin-top: 1rem;
  }
}
</style>
